import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import {
  AnalysisSet,
  AnalysisSetGuidWithQuantity,
  Job,
  ProjectSnapshot,
  SharedComponentSet,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  buildProjectSynchronizationPayload,
  migrateIncomingProject,
  projectSynchronized,
} from '@paragon/layout-models';
import { getClientUrls } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { naturalSortByProperty } from '@paragon/utilities';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import { getProjectLayoutModel } from '../selectors/getProjectLayoutModel';
import { sharedComponentSetHasOutdatedComponentsWarning } from '../selectors/sharedComponentSets';
import { setAnalysisHistory } from '../slices/analysisHistorySlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setDimensionSets } from '../slices/dimensionsSlice';
import { setJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import { layoutSlice } from '../slices/layoutSlice';
import { setLoadSets } from '../slices/loadSetsSlice';
import { setAnalysisSettingsFromObject, setLoadSettingsFromObject } from '../slices/settingsDatabaseSlice';
import {
  errorLoadingSharedComponentSet,
  setComponentDesignGuidsThatNoLongerExistInSourceProject,
  setProjectSnapshot,
  setSharedComponentSetIsMissingJacks,
  sharedComponentSetLoaded,
} from '../slices/sharedComponentSetSlice';
import { trussGroupsLoaded } from '../slices/trussGroupsSlice';
import { ensureCorrectSelectionAfterProjectLoad, loadAnalysisSetByGuid, receiveAnalysisSet } from './loadActions';
import { showLogInDialog } from './popupActions';
import { selectEntry } from './selectActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export const loadSharedComponentSetContext =
  (sharedComponentSetLookupCode: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading...'));

    let sharedComponentSet: SharedComponentSet;
    try {
      sharedComponentSet = await designService.sharedComponentSets.fetch(sharedComponentSetLookupCode);
      dispatch(sharedComponentSetLoaded(sharedComponentSet));
    } catch (error) {
      console.error(error);
      dispatch(errorLoadingSharedComponentSet());
      dispatch(removeSpinnerMessage(spinnerId));
      return;
    }

    try {
      const projectSnapshotPromise = dispatch(loadProjectSnapshot(sharedComponentSet));

      // Load the active AnalysisSet first so the user sees it sooner
      const queryParameters = new URLSearchParams(window.location.search);
      const analysisSetGuidFromUrl = queryParameters.get('analysisSetGuid');
      const loadFirstAnalysisSetPromise = analysisSetGuidFromUrl
        ? dispatch(
            loadAnalysisSetByGuid(analysisSetGuidFromUrl, {
              setAnalysisSetAsActive: true,
              forceUpdateWorkingChanges: true,
              skipJackGirderConnections: true, // loaded later with the rest of the AnalysisSets
              skipSettings: true,
            }),
          )
        : Promise.resolve();

      const {
        analysisSets,
        history,
        timestamps,
        carriedComponentConnections,
        loadSets,
        dimensionSets,
        componentDesignGuidsNoLongerInProject,
        loadSettingsByComponentDesignGuid,
        analysisSettingsByComponentDesignGuid,
      } = await designService.sharedComponentSets.fetchAnalysisSetsWithInformation(sharedComponentSetLookupCode);
      await loadFirstAnalysisSetPromise;

      dispatch(setAnalysisHistory(history));
      dispatch(setComponentDesignTimestamps({ timestamps }));
      dispatch(setLoadSets(loadSets));
      dispatch(setDimensionSets(dimensionSets));
      dispatch(setComponentDesignGuidsThatNoLongerExistInSourceProject(componentDesignGuidsNoLongerInProject));
      dispatch(setLoadSettingsFromObject(loadSettingsByComponentDesignGuid));
      dispatch(setAnalysisSettingsFromObject(analysisSettingsByComponentDesignGuid));

      let alreadyReceivedAnalysisSet: AnalysisSet | null = null;
      if (!analysisSetGuidFromUrl) {
        let analysisSetIdentifiedByUrl: AnalysisSet | null = null;
        const activeComponentDesignGuid = getActiveComponentDesignGuid(getState());
        const indexQueryParameter = queryParameters.get('i');
        if (activeComponentDesignGuid) {
          analysisSetIdentifiedByUrl =
            analysisSets.find((analysisSet) => analysisSet.componentDesign.guid === activeComponentDesignGuid) ?? null;
        } else if (indexQueryParameter) {
          const analysisSetGuid =
            sharedComponentSet.analysisSetGuidsWithQuantities[parseInt(indexQueryParameter, 10)]?.analysisSetGuid;
          analysisSetIdentifiedByUrl = analysisSetGuid
            ? (analysisSets.find((analysisSet) => analysisSet.guid === analysisSetGuid) ?? null)
            : null;
        }

        if (analysisSetIdentifiedByUrl) {
          // The URL specified an index into the SharedComponentSet's components or a ComponentDesign GUID; receive and
          // select that one first.
          await dispatch(
            receiveAnalysisSet(analysisSetIdentifiedByUrl, {
              setAnalysisSetAsActive: !!activeComponentDesignGuid,
              replaceWorkingChanges: true,
              skipJackGirderConnections: true,
            }),
          );
          if (!activeComponentDesignGuid) {
            dispatch(selectEntry({ type: 'componentDesign', item: analysisSetIdentifiedByUrl.componentDesign }));
          }
          alreadyReceivedAnalysisSet = analysisSetIdentifiedByUrl;
        } else {
          // In the absence of a component identified by the URL, show the layout if it exists, or the first component
          // alphabetically
          const projectSnapshot = await projectSnapshotPromise;
          if (projectSnapshot?.layout) {
            const selectedItems = getSelectedItemIdentifiers(getState());
            // Only select the project if the URL does not indicate another selection
            if (selectedItems.length === 0) {
              const projectLayoutModel = getProjectLayoutModel(getState());
              if (projectLayoutModel) dispatch(selectEntry({ type: 'project', item: projectLayoutModel }));
              else console.error('Project not found in SharedComponentSet');
            }
          } else {
            const alphabeticallyFirstAnalysisSet = naturalSortByProperty(
              analysisSets,
              (analysisSet) => analysisSet.componentDesign.component.name,
            )[0];
            await dispatch(
              receiveAnalysisSet(alphabeticallyFirstAnalysisSet, {
                setAnalysisSetAsActive: true,
                replaceWorkingChanges: true,
                skipJackGirderConnections: true,
              }),
            );
            dispatch(selectEntry({ type: 'componentDesign', item: alphabeticallyFirstAnalysisSet.componentDesign }));
            alreadyReceivedAnalysisSet = alphabeticallyFirstAnalysisSet;
          }
        }
      }

      const MAX_ACTIVE_RECEIVE_ANALYSIS_SET_WORKERS = 10;
      const analysisSetsToLoad = analysisSets.filter(
        (analysisSet) =>
          analysisSet.guid !== alreadyReceivedAnalysisSet?.guid && analysisSet.guid !== analysisSetGuidFromUrl,
      );
      const receiveAnalysisSetWorkers: Promise<void>[] = [];
      for (let i = 0; i < MAX_ACTIVE_RECEIVE_ANALYSIS_SET_WORKERS; i++) {
        receiveAnalysisSetWorkers.push(dispatch(receiveAnalysisSetWorker(analysisSetsToLoad)));
      }

      for (const worker of receiveAnalysisSetWorkers) {
        await worker;
      }

      // If any components involved in carried component relationships were not included in the SharedComponentSet, do
      // not load connections that include those excluded components
      const projectSnapshot = await projectSnapshotPromise;
      const componentDesignGuids = analysisSets.map((analysisSet) => analysisSet.componentDesign.guid);
      const connections = projectSnapshot?.carriedComponentConnections ?? carriedComponentConnections;
      const relevantCarriedComponentConnections = connections.filter(
        (carriedComponentConnection) =>
          componentDesignGuids.includes(carriedComponentConnection.jackGuid) &&
          componentDesignGuids.includes(carriedComponentConnection.girderGuid),
      );
      dispatch(setJackGirderConnections(relevantCarriedComponentConnections));

      // this check must happen AFTER everything is loaded since it examines the selected entry which could be null if
      //   relevant data isn't in state yet
      if (projectSnapshot) {
        dispatch(ensureCorrectSelectionAfterProjectLoad(projectSnapshot.guid));
      }

      const sharedComponentSetIsMissingJacks = connections.some(
        (connection) =>
          componentDesignGuids.includes(connection.girderGuid) && !componentDesignGuids.includes(connection.jackGuid),
      );
      dispatch(setSharedComponentSetIsMissingJacks(sharedComponentSetIsMissingJacks));

      const outdatedComponents = sharedComponentSetHasOutdatedComponentsWarning(getState());
      if (outdatedComponents && outdatedComponents.length > 0) {
        const [firstOutdatedComponent, ...remainingOutdatedComponents] = outdatedComponents;
        let outdatedComponentsListTruncated = firstOutdatedComponent;
        for (const outdatedComponent of remainingOutdatedComponents) {
          const nextItem = `, ${outdatedComponent}`;
          if (outdatedComponentsListTruncated.length + nextItem.length > 100) {
            outdatedComponentsListTruncated += ', etc';
            break;
          }
          outdatedComponentsListTruncated += nextItem;
        }

        dispatch(
          enqueueWarning(
            `Components have changed at source: ${outdatedComponentsListTruncated}. Contact ${sharedComponentSet.contactEmail} to request an updated set or for more information.`,
          ),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(
            error,
            'Error loading data - contact help@paragontruss.com and include this URL',
          ),
        ),
      );

      dispatch(showLogInDialog());
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

const receiveAnalysisSetWorker =
  (analysisSetStack: AnalysisSet[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    while (analysisSetStack.length > 0) {
      const analysisSet = analysisSetStack.pop();
      if (!analysisSet) return;

      await dispatch(
        receiveAnalysisSet(analysisSet, {
          setAnalysisSetAsActive: false,
          replaceWorkingChanges: true,
          skipJackGirderConnections: true,
        }),
      );
    }
  };

const loadProjectSnapshot =
  (sharedComponentSet: SharedComponentSet): AsyncThunkActionReturn<ProjectSnapshot | null> =>
  async (dispatch, getState) => {
    if (!sharedComponentSet.projectSnapshotGuid) return null;

    try {
      const projectSnapshot = await designService.sharedComponentSets.fetchProjectSnapshot(
        sharedComponentSet.lookupCode,
      );
      dispatch(setProjectSnapshot(projectSnapshot));
      const { project, layout } = projectSnapshot;

      const projectSynchronizationPayload = buildProjectSynchronizationPayload(
        getState(),
        migrateIncomingProject(project),
        project.guid,
        {
          // Intentionally provide empty values here to avoid loading these; we handle loading these elsewhere
          componentDesignData: { layoutModels: [], timestamps: [] },
        },
      );
      dispatch(
        // This is how projects are loaded, but we're not joining any room to receive updates; this is a snapshot
        projectSynchronized({
          [project.guid]: projectSynchronizationPayload,
        }),
      );

      if (layout) dispatch(layoutSlice.actions.layoutDataLoaded(layout));
      dispatch(trussGroupsLoaded(Object.values(project.trussGroups)));

      return projectSnapshot;
    } catch (error) {
      // If something fails about loading the layout, we would rather show the trusses than fail to load everything.
      console.error(error);
      dispatch(enqueueError('Error loading layout data. Contact help@paragontruss.com.'));
      return null;
    }
  };

export const createSharedComponentSet =
  (
    name: string,
    analysisSetGuidsWithQuantities: readonly AnalysisSetGuidWithQuantity[],
    componentDesignGuids: readonly string[],
    shareProjectSnapshot: boolean,
  ): AsyncThunkActionReturn<SharedComponentSet | null> =>
  async (dispatch, getState) => {
    const project = getProjectLayoutModel(getState());

    try {
      const newSharedComponentSet = await designService.sharedComponentSets.create(
        name,
        analysisSetGuidsWithQuantities,
        componentDesignGuids,
        project?.guid ?? null,
        shareProjectSnapshot,
      );
      dispatch(enqueueSuccess('Shared component set created!'));
      return newSharedComponentSet;
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error creating shared component set'));
      return null;
    }
  };

export const copyCurrentSharedComponentSetToMyOrganization = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const sharedComponentSetLookupCode = getState().sharedComponentSet.sharedComponentSet?.lookupCode;
  if (!sharedComponentSetLookupCode) throw new Error('Not in a SharedComponentSet context');

  dispatch(copySharedComponentSetToMyOrganization(sharedComponentSetLookupCode));
};

export const copySharedComponentSetToMyOrganization =
  (lookupCode: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Copying to your organization...'));

    try {
      const projectGuid = await designService.sharedComponentSets.copyToMyOrganization(lookupCode);
      window.open(`${getClientUrls(getState()).designUrl}${projectGuid}`, '_blank');
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error copying shared component set')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export type JobCreationResult = ({ error: true } | { error: false; jobId: number }) & { userVisibleMessage: string };
export type JobCreationPromise = Promise<JobCreationResult>;

export const createJobFromSharedComponentSet =
  (
    jobType: 'Seal' | 'Repair',
    sharedComponentSet: SharedComponentSet,
    sealingOrganization: string,
    requiredProperties: Pick<Job, 'regions' | 'contactName' | 'contactPhone' | 'contactEmail'>,
    comments: string | null,
  ): AsyncThunkActionReturn<JobCreationResult> =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.logInData.loggedInUser == null) {
      return { error: true, userVisibleMessage: 'You must be logged in to create a job.' };
    }

    if (sharedComponentSet.sourceProjectGuid == null) {
      return { error: true, userVisibleMessage: 'That shared component set is not suitable for creating a job.' };
    }

    const job: Partial<Job> = {
      ...requiredProperties,
      name: sharedComponentSet.name,
      jobType,
      jobPriority: 'Normal',
      designProgram: 'Paragon',
      ownerOrganization: state.logInData.loggedInUser.organization,
      numberOfDesigns: Object.keys(sharedComponentSet.analysisSetGuidsWithQuantities).length,
      billingLocation: state.logInData.loggedInUser.location,
      sourceProjectGuid: sharedComponentSet.sourceProjectGuid,
      comments,
    };

    const spinnerId = dispatch(addSpinnerMessage('Creating job...'));

    try {
      const jobId = await designService.componentDesigns.postCreateJobFromExistingComponentSet({
        jobType,
        sharedComponentSetLookupCode: sharedComponentSet.lookupCode,
        // FIXME: allowing choice of templates will be important for G2
        trussDesignDrawingTemplateId: null,
        trussDesignDrawingOverflowTemplateId: null,
        serializedJobForUpload: JSON.stringify(job),
        sourceProjectGuid: sharedComponentSet.sourceProjectGuid,
        name: sharedComponentSet.name,
        shareWithOrganization: sealingOrganization,
        sendEmailForAnalysisErrorsOrOverCapacity: false, // We rely on the user seeing the warning in CreateJobForm.tsx
      });

      return {
        jobId,
        error: false,
        userVisibleMessage:
          `The job has been shared with ${sealingOrganization}. If this is their first time working with your ` +
          'organization, they will be reaching out to you for more information before starting on the job.',
      };
    } catch (error) {
      console.error(error);

      return { error: true, userVisibleMessage: 'Error creating job. Please contact Paragon Customer Support.' };
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
